/**
 * Financial Reports service — Notes, Schedules & Reconciliation.
 *
 * Companion to financeService for the Statutory Control Center's supporting
 * schedules and the data-integrity reconciliation pass. Uses the shared
 * apiClient (auto-unwraps the { status, data } envelope). All figures are real
 * — sourced from POSTED journal lines / sub-ledgers on the backend.
 */

import { api } from './apiClient';

export interface AgeingBuckets {
  current: number;
  days31_60: number;
  days61_90: number;
  days91_120: number;
  over120: number;
}
export interface AgeingTotals extends AgeingBuckets {
  total: number;
}

export interface FixedAssetSchedule {
  asOnDate: string;
  period: { fromDate: string; toDate: string };
  grossBlock: { opening: number; additions: number; disposals: number; closing: number };
  depreciation: { opening: number; chargeForPeriod: number; onDisposals: number; closing: number };
  capitalWip: { opening: number; closing: number };
  netBlock: number;
  disposalProceeds: number;
  disposalCount: number;
}

export interface ControlAccountSchedule {
  asOnDate: string;
  controlAccount: { code: string; name: string; glBalance: number };
  ageing: { totals: AgeingTotals; customers?: any[]; vendors?: any[] };
  subLedgerTotal: number;
  difference: number;
  tiesToGL: boolean;
}

export interface InventorySchedule {
  asOnDate: string;
  items: { name: string; code: string; amount: number }[];
  glTotal: number;
  valuationSnapshotTotal: number | null;
}

export interface BorrowingsSchedule {
  asOnDate: string;
  items: { name: string; code: string; amount: number }[];
  total: number;
}

export interface NoteRow {
  note: number;
  title: string;
  statement: string;
  current: number;
  previous: number | null;
  detail?: any;
}
export interface NotesToAccounts {
  asOnDate: string;
  period: { fromDate: string; toDate: string };
  comparativeDate: string | null;
  notes: NoteRow[];
}

export interface ReconciliationCheck {
  label: string;
  left: number;
  right: number;
  difference: number;
  status: 'PASS' | 'WARN';
  blocking: boolean;
}
export interface Reconciliation {
  asOnDate: string;
  checks: ReconciliationCheck[];
  summary: {
    total: number;
    passed: number;
    warnings: number;
    blockingFailures: number;
    overall: 'PASS' | 'WARN' | 'FAIL';
  };
  notes: string;
}

const q = (params: Record<string, string | undefined>): string => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

export const financialReportsService = {
  getFixedAssetSchedule: (asOnDate: string, fromDate?: string): Promise<FixedAssetSchedule> =>
    api.get(`/finance/reports/schedules/fixed-assets${q({ asOnDate, fromDate })}`),

  getReceivablesSchedule: (asOnDate: string): Promise<ControlAccountSchedule> =>
    api.get(`/finance/reports/schedules/receivables${q({ asOnDate })}`),

  getPayablesSchedule: (asOnDate: string): Promise<ControlAccountSchedule> =>
    api.get(`/finance/reports/schedules/payables${q({ asOnDate })}`),

  getInventorySchedule: (asOnDate: string): Promise<InventorySchedule> =>
    api.get(`/finance/reports/schedules/inventory${q({ asOnDate })}`),

  getBorrowingsSchedule: (asOnDate: string): Promise<BorrowingsSchedule> =>
    api.get(`/finance/reports/schedules/borrowings${q({ asOnDate })}`),

  getNotesToAccounts: (
    asOnDate: string,
    fromDate?: string,
    toDate?: string,
    comparativeDate?: string
  ): Promise<NotesToAccounts> =>
    api.get(`/finance/reports/notes${q({ asOnDate, fromDate, toDate, comparativeDate })}`),

  getReconciliation: (asOnDate: string): Promise<Reconciliation> =>
    api.get(`/finance/reports/reconciliation${q({ asOnDate })}`),
};

export default financialReportsService;
